import type { components } from '@/generated/internal-api/openapi-types';
import type { StudyApiId } from '@/model/Study.model';
import type {
  RepeatOnDays,
  RepeatUnit,
  TaskScheduleConditionalType,
  TaskSpecCreate,
  TaskTimeDuration,
  TaskType,
} from '@/model/Task.model';
import type { GroupApiId, TreatmentGroup } from '@/model/TreatmentGroup.model';
import type { BaseUser, UserApiId } from '@/model/User.model';
import { type ReactNode } from 'react';

export interface StepFormPayloadProps {
  overview: AddTaskOverviewData;
  execution: AddTaskExecutionData;
  assignees: AddTaskAssgineeData;
  schedule: AddTaskScheduleData;
  target: AddTaskTargetData;
}

export type AddTaskFormTypes = keyof StepFormPayloadProps;

export type ScheduleTypes = 'one_off' | 'conditional' | 'recurring';
export type TargetTypeValues = 'animal' | 'group';

export type TaskSpecFieldNames =
  | 'overview.type'
  | 'schedule.type'
  | 'schedule.repeat'
  | 'schedule.repeat.days'
  | 'schedule.condition.type'
  | 'target.groups'
  | 'target.type';

export interface AddTaskOverviewData {
  title: string;
  description: string;
  type?: TaskType;
}

export type WorkflowTemplateMeasurementV1 = components['schemas']['WorkflowTemplateMeasurementV1.schema'];
export type WorkflowTemplateSampleV1 = components['schemas']['WorkflowTemplateSampleV1.schema'];
export type WorkflowTemplateObservationV1 = components['schemas']['WorkflowTemplateObservationV1.schema'];
export type WorkflowTemplateDosingV1 = components['schemas']['WorkflowTemplateDosingV1.schema'];

export type AddTaskExecutionData = Partial<
  WorkflowTemplateMeasurementV1 | WorkflowTemplateSampleV1 | WorkflowTemplateObservationV1 | WorkflowTemplateDosingV1
>;

export type AddTaskAssgineeData = UserApiId[];

export interface AddTaskScheduleData {
  type: ScheduleTypes;
  start?: string;
  end?: string;
  time?: TaskTimeDuration;
  repeat?: {
    value: number;
    unit: RepeatUnit;
    days?: RepeatOnDays;
  };
  condition?: {
    type: TaskScheduleConditionalType;
    value: number[];
  };
}

export interface AddTaskTargetData {
  type: TargetTypeValues;
  groups?: GroupApiId[];
}

export interface IStep {
  name: AddTaskFormTypes;
  title: string;
  display: (taskType?: TaskType) => boolean;
  render: (props: StepRenderProps) => ReactNode;
}

export type OptionalStepFormPayloadProps = Partial<StepFormPayloadProps>;

export type StudyUsersApi = components['schemas']['StudyUserSummaryV1.schema'][];
export type TaskSpecUser = Pick<BaseUser, 'api_id' | 'name' | 'email'>;

/** @Todo: Remove `studyId` when Groups API is migrated to Java */
export interface AddTaskProps {
  /* For existing studies to fetch legacy resources eg. Groups API */
  studyId?: number;
  studyApiId?: StudyApiId;
  /** studyStarted on will default the `begins` field */
  studyStartedOn?: string;
  /** Study-level tracking day base, used as day 0 reference for tracking-day schedules */
  trackingDayBase?: number;
  /* For study creation form, after users have been assigned */
  studyCreationUsers?: TaskSpecUser[];
  teamUsers?: TaskSpecUser[];
  /* For study creation form, after groups have been input */
  studyCreationGroups?: Array<TreatmentGroup>;
  execution?: {
    measurements: Array<{ id: string; title: string }>;
    samples: Array<{ id: string; title: string }>;
    observations: Array<{ id: string; title: string }>;
    treatments: Array<{ id: string; title: string }>;
    team_glossary_api_ids?: Array<string>;
  };
  handleSubmit?: (addTaskStepFormData: TaskSpecCreate) => Promise<void> | void;
  closeModal: () => void;
}

export interface StepRenderProps {
  onSubmit: (payload: OptionalStepFormPayloadProps) => Promise<void> | void;
  onCancel: () => void;
  submitButtonText: string;
  cancelButtonText: string;
  studyId?: AddTaskProps['studyId'];
  studyApiId?: AddTaskProps['studyApiId'];
  trackingDayBase?: AddTaskProps['trackingDayBase'];
  studyCreationUsers?: AddTaskProps['studyCreationUsers'];
  teamUsers?: AddTaskProps['studyCreationUsers'];
  studyCreationGroups?: AddTaskProps['studyCreationGroups'];
  execution?: AddTaskProps['execution'];
  /** When editing a task spec in an existing study, specific fields will be disabled, this will change as we add more API coverage  */
  disabledFields?: Set<TaskSpecFieldNames>;
}
